/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import { createTheme } from '@mui/material/styles';
import { red } from '@mui/material/colors';

// Extend the theme type to include custom properties
declare module '@mui/material/styles' {
	interface TypographyVariants {
		fontFamilyBold: string;
	}
	interface TypographyVariantsOptions {
		fontFamilyBold?: string;
	}
}

// Create a theme instance.
const theme = createTheme({
	palette: {
		primary: {
			main: '#F4B70B',
		},
		secondary: {
			main: '#202164',
			light: '#252673',
			dark: '#0C0C26',
		},
		error: {
			main: red.A400,
		},
		background: {
			default: '#F5F5F7',
			paper: '#FFF',
		},
		info: {
			main: '#000',
		},
	},
	typography: {
		fontFamily: '"M PLUS Rounded 1c", sans-serif',
		fontFamilyBold: '"M PLUS Rounded 1c Bold", sans-serif',
		fontWeightBold: 610,
		body1: {
			color: '#0C0C26',
		},
		body2: {
			color: 'rgba(0,0,0, 0.6)',
			fontSize: '.9rem',
		},
		subtitle1: {
			color: '#FFFFFF',
			fontSize: '1.125rem',
		},
	},
	breakpoints: {
		values: {
			xs: 0,
			sm: 670,
			md: 960,
			lg: 1280,
			xl: 1920,
		},
	},
	components: {
		MuiTypography: {
			styleOverrides: {
				h1: {
					fontFamily: '"M PLUS Rounded 1c Bold", sans-serif',
					fontSize: '1.375rem',
					color: '#0C0C26',
					letterSpacing: '-0.8px',
					lineHeight: 'unset',
				},
				h2: {
					fontFamily: '"M PLUS Rounded 1c Bold", sans-serif',
					fontSize: '0.9rem',
					color: '#121003',
					letterSpacing: '-0.8px',
				},
				h3: {
					fontFamily: '"M PLUS Rounded 1c Bold", sans-serif',
					fontSize: '1.0625rem',
					color: '#121003',
					letterSpacing: '-0.8px',
				},
			},
		},
		MuiFormControl: {
			styleOverrides: {
				root: {
					borderRadius: '8px',
					backgroundColor: '#fff',
				},
			},
		},
		MuiLink: {
			styleOverrides: {
				underlineHover: {
					'&:hover': {
						color: '#202164',
					},
				},
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					borderRadius: '8px',
				},
			},
		},
		MuiButton: {
			styleOverrides: {
				root: {
					textTransform: 'none',
				},
				text: {
					fontWeight: 'bold',
				},
				contained: {
					fontWeight: 'bold',
				},
				outlined: {
					fontWeight: 'bold',
				},
				outlinedSecondary: {
					textDecoration: 'underline',
					border: '1px solid rgba(255, 255, 255, 0.2)',
					'&:hover': {
						border: '1px solid rgba(255, 255, 255, 0.4)',
					},
				},
			},
		},
		MuiFormControlLabel: {
			styleOverrides: {
				label: {
					fontSize: '0.75rem',
				},
			},
		},
		MuiPaper: {
			styleOverrides: {
				rounded: {
					borderRadius: '8px',
				},
			},
		},
		MuiTableContainer: {
			styleOverrides: {
				root: {
					overflowX: 'inherit',
				},
			},
		},
		MuiTableHead: {
			styleOverrides: {
				root: {
					backgroundColor: '#E1E1E3',
					'& th': {
						fontFamily: '"M PLUS Rounded 1c Bold", sans-serif',
					},
				},
			},
		},
		MuiTableRow: {
			styleOverrides: {
				root: {
					'&:nth-of-type(even)': {
						backgroundColor: '#E1E1E3',
					},
					'&.Mui-selected, &.Mui-selected:hover': {
						backgroundColor: 'rgba(244, 183, 11, 0.18) !important',
					},
				},
			},
		},
		MuiTableCell: {
			styleOverrides: {
				root: {
					padding: '10px',
				},
			},
		},
		MuiBackdrop: {
			styleOverrides: {
				root: {
					backgroundColor: 'rgba(0, 0, 0, 0.6)',
				},
			},
		},
	},
});

export default theme;
